/*
 * Reglas de cálculo del modelo en la ficha. El formulario (FormV2) corre las
 * reglas de la definición en cada cambio y guarda los derivados junto con el
 * resto; la ficha guarda de a un campo, así que al guardar corre las mismas
 * reglas sobre el documento entero (lo guardado + el cambio) y manda también
 * lo que recalculan (p. ej. aplicado = suma de Applied to, saldo = monto − aplicado).
 */
import {
  applyCalculationRules,
  getValueFromState,
  ruleIdToPath,
} from "@/components/gotrading/common/formCalculationRules";
import { getValueByPath, setValueByPath } from "@/components/gotrading/common/useFormEngine";

/**
 * Las reglas nombran los campos con o sin el grupo visual delante
 * («group_details-applied» o «amount»); en el item todos van planos.
 */
function makeFlatPath(visualGroups) {
  const prefixes = new Set((visualGroups || []).map((g) => `group_${g.name}`));
  return (path) => {
    const parts = String(path || "").split(".");
    if (parts.length > 1 && prefixes.has(parts[0])) parts.shift();
    return parts.join(".");
  };
}

/** Campos (de primer nivel) que alguna regla escribe. */
export function ruleTargetKeys(rules, visualGroups) {
  const flat = makeFlatPath(visualGroups);
  const keys = new Set();
  for (const r of rules || []) {
    if (r.isActive === false) continue;
    const dest = flat(ruleIdToPath(r.destination || r.targetField || ""));
    if (dest && !dest.includes(".")) keys.add(dest);
  }
  return keys;
}

/** Campos (de primer nivel) que alguna regla lee. */
export function ruleSourceKeys(rules, visualGroups) {
  const flat = makeFlatPath(visualGroups);
  const keys = new Set();
  for (const r of rules || []) {
    if (r.isActive === false) continue;
    const ids = String(r.pattern || r.action || "").match(/[a-zA-Z][a-zA-Z0-9_\-*]*/g) || [];
    for (const id of ids) {
      const first = flat(ruleIdToPath(id)).split(".")[0];
      if (first) keys.add(first);
    }
  }
  return keys;
}

/**
 * @param {object} doc documento completo ya fusionado con el cambio (con las filas
 *   de los grupos como objetos, no ids)
 * @returns {object} campos derivados que cambian respecto de `before`
 */
export function computeRuleDerivedValues({ doc, before, rules, visualGroups, changedKeys }) {
  const active = (rules || []).filter((r) => r.isActive !== false);
  if (!active.length) return {};
  const flat = makeFlatPath(visualGroups);
  const state = JSON.parse(JSON.stringify(doc || {}));
  // Lecturas con comodín que el motor hace directo sobre el estado:
  // state.group_details.appliedto → state.appliedto
  for (const g of visualGroups || []) {
    Object.defineProperty(state, `group_${g.name}`, {
      get: () => state,
      enumerable: false,
      configurable: true,
    });
  }
  const get = (path) => {
    const p = flat(path);
    return p.includes("*") ? getValueFromState(state, p) : getValueByPath(state, p);
  };
  const set = (path, value) => setValueByPath(state, flat(path), value);
  applyCalculationRules(state, active, get, set);

  const derived = {};
  const skip = new Set(changedKeys || []);
  for (const key of ruleTargetKeys(active, visualGroups)) {
    if (skip.has(key)) continue;
    const next = state[key];
    const prev = before ? before[key] : undefined;
    const same =
      next === prev ||
      (Number.isFinite(Number(next)) &&
        Number.isFinite(Number(prev)) &&
        Number(next) === Number(prev));
    if (!same) derived[key] = next;
  }
  return derived;
}
